// WorkOrdersPage.js
import React, { useState, useEffect, useCallback } from "react";
import Card from "../../Components/Card/Card";
import "./workorderpage.css";
import WorkOrderForm from "../../Components/WorkOrderForm/work-order-form";
import { fetchEmployees, fetchDailyWorkOrders, fetchAllWorkOrders, updateWorkOrder } from "../../Utils/api";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import PopupGeneric from '../../Components/PopupGeneric/pop-up-generic';
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { IconButton } from "@mui/material"; // Add this import
import ArrowBackIcon from "@mui/icons-material/ArrowBack"; // Add this import
import SortIcon from '@mui/icons-material/Sort';
import { useAuth } from "../../Auth/AuthContext";

const WorkOrdersPage = () => {
  const navigate = useNavigate(); // Initialize useNavigate

  // Generate today's date dynamically
  const getTodayDate = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`; // Format: YYYY-MM-DD
  };
  const formatDate = (dateString) => {
    if (!dateString) return 'No date set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };


  const todayDate = getTodayDate(); // Today's date in YYYY-MM-DD format

  const [workOrders, setWorkOrders] = useState([]); // All work orders fetched from the table
  const [todayWorkOrders, setTodayWorkOrders] = useState([]); // work orders for today's date
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null); 
  const [isCancelPopupVisible, setCancelPopupVisible] = useState(false);
  const [workOrderToCancel, setWorkOrderToCancel] = useState(null); // Work order to cancel
  const [activeTab, setActiveTab] = useState("today"); // State for active tab
  const [sortOrder, setSortOrder] = useState("desc"); // default: newest first
  const { companyId } = useAuth();

  // Fetch all work orders from the table
  const fetchWorkOrders = useCallback(async () => {
    try {
      const response = await fetchAllWorkOrders(companyId); // Fetch all work orders
      if (Array.isArray(response)) {
        const technicianList = await fetchEmployees(companyId, null, "Technician");
        const formattedWorkOrders = response.map((item) => ({
          customerName: `${item.customer.custFirstName} ${item.customer.custLastName}`,
          address: `${item.customer.custAddress}, ${item.customer.custCity}, ${item.customer.custState} ${item.customer.custZip}`,
          assigneeName: `${item.employee.firstName} ${item.employee.lastName}`,
          assignee: item.employee,
          status: item.workOrder.statusCode,
          workOrder: item.workOrder,
          workOrderDate: item.workOrder.initialServiceDate,
          workOrderType: item.workOrder.workOrderType,
          workOrderComment: item.workOrder.workOrderComment,
          employeeId: item.employee.employeeId,
          customerId: item.customer.customerId,
          employeeList: technicianList,
        }));
        setWorkOrders(formattedWorkOrders);
      } else {
        setWorkOrders([]);
      }
    } catch (error) {
      console.error("Failed to load all work orders", error);
      setWorkOrders([]);
    }
  }, [companyId]);

  // Fetch work orders for today's date
  const fetchTodayWorkOrders = useCallback(async () => {
    try {
      const response = await fetchDailyWorkOrders(companyId, todayDate); // Use today's date
      if (Array.isArray(response)) {
        const technicianList = await fetchEmployees(companyId, null, "Technician");
        const formattedWorkOrders = response.map((item) => ({
          customerName: `${item.customer.custFirstName} ${item.customer.custLastName}`,
          address: `${item.customer.custAddress}, ${item.customer.custCity}, ${item.customer.custState} ${item.customer.custZip}`,
          assigneeName: `${item.employee.firstName} ${item.employee.lastName}`,
          assignee: item.employee,
          status: item.workOrder.statusCode,
          workOrder: item.workOrder,
          workOrderDate: item.workOrder.initialServiceDate,
          workOrderType: item.workOrder.workOrderType,
          workOrderComment: item.workOrder.workOrderComment,
          employeeId: item.employee.employeeId,
          customerId: item.customer.customerId,
          employeeList: technicianList,
        }));
        setTodayWorkOrders(formattedWorkOrders);
      } else {
        setTodayWorkOrders([]);
      }
    } catch (error) {
      console.error("Failed to load today's work orders", error);
      setTodayWorkOrders([]);
    }
  }, [companyId, todayDate]);

  useEffect(() => {
    fetchWorkOrders(); // Fetch all tasks for the "Overall" tab
    fetchTodayWorkOrders(); // Fetch tasks for today's date
  }, [fetchWorkOrders, fetchTodayWorkOrders]);

  // Tasks to display based on the active tab
  const displayedWorkOrders = (activeTab === "today" ? todayWorkOrders : workOrders)
  .slice() // make a shallow copy before sorting
  .sort((a, b) => {
    const dateA = new Date(a.workOrderDate);
    const dateB = new Date(b.workOrderDate);
    return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
  });

  const onConfirmCancel = async () => {
    if (!workOrderToCancel) return;

    const updatedData = {
      ...workOrderToCancel.workOrder,
      workOrderId: workOrderToCancel.workOrder.workOrderId,
      statusCode: "Cancelled",
    };
    try {
      await updateWorkOrder(updatedData);
      fetchWorkOrders(); // Refresh all tasks
      fetchTodayWorkOrders(); // Refresh today's tasks
    } catch (error) {
      console.error("Error cancelling work order:", error);
    }
    setCancelPopupVisible(false);
    setWorkOrderToCancel(null);
  };

  const onCancelPopupClose = () => {
    setCancelPopupVisible(false);
    setWorkOrderToCancel(null);
  };

  const onEdit = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setModalOpen(true);
  };

  const onCancelClick = (workOrder) => {
    setWorkOrderToCancel(workOrder);
    setCancelPopupVisible(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedWorkOrder(null);
  };

  const handleFormSubmit = async (updatedWorkOrder) => {
    const originalWorkOrder = selectedWorkOrder.workOrder;
    const payload = {
      workOrderId: originalWorkOrder.workOrderId,
      poolId: originalWorkOrder.poolId,
      customerId: originalWorkOrder.customerId,
      createdAt: originalWorkOrder.createdAt,
      updatedAt: originalWorkOrder.updatedAt,
      statusCode: originalWorkOrder.statusCode,
      lastServiceDate: originalWorkOrder.lastServiceDate,
      workOrderDueDate: originalWorkOrder.workOrderDueDate,
      workOrderCost: originalWorkOrder.workOrderCost,
      workOrderPart: originalWorkOrder.workOrderPart,
      workOrderServiceType: "string",
      employeeId: updatedWorkOrder.selectedTechnician?.employeeId
        ? updatedWorkOrder.selectedTechnician.employeeId
        : originalWorkOrder.employeeId,
      workOrderType: updatedWorkOrder.selectedTask || originalWorkOrder.workOrderType,
      workOrderComment:
      updatedWorkOrder.additionalComments || originalWorkOrder.workOrderComment,
      initialServiceDate: updatedWorkOrder.date
        ? updatedWorkOrder.date.format("YYYY-MM-DDTHH:mm:ss")
        : originalWorkOrder.initialServiceDate,
    };

    try {
      await updateWorkOrder(payload);
      fetchWorkOrders(); // Refresh all work orders
      fetchTodayWorkOrders(); // Refresh today's work orders
      handleModalClose();
    } catch (error) {
      console.error("Error updating work order:", error);
    }
  };

  return (
    <div className="work-orders-page">

       {/* Tabs Container */}
       <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '1rem',
        position: 'relative', // For absolute positioning of back button
        marginTop:5
      }}>

        {/* Centered Tabs */}
        <div style={{
          display: 'flex',
          margin: '0 auto' ,// This centers the tabs

        }}>
          <button
            className={`tab-button ${activeTab === "today" ? "active" : ""}`}
            onClick={() => setActiveTab("today")}
          >
            Today ({todayWorkOrders.length})
          </button>
          <button
            className={`tab-button ${activeTab === "overall" ? "active" : ""}`}
            onClick={() => setActiveTab("overall")}
          >
            Overall ({workOrders.length})
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 20px', marginBottom: '10px' }}>
  <button
  onClick={() => setSortOrder(prev => prev === "asc" ? "desc" : "asc")}
  style={{
    padding: '8px 16px',
    backgroundColor: '#1d5ca7',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }}
>
  <SortIcon 
    style={{
      transform: sortOrder === "asc" ? "rotate(180deg)" : "rotate(0deg)",
      transition: 'transform 0.3s ease'
    }}
  />
 
</button>
</div>
      </div>

        {/* Cards Grid Container */}
        <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
        gap: '20px',
        padding: '0 20px 20px',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {displayedWorkOrders.map((workOrder, index) => (
          <Card
            key={index}
            customerName={workOrder.customerName}
            address={workOrder.address}
            assignee={workOrder.assigneeName}
            status={workOrder.status}
            date={formatDate(workOrder.workOrderDate)}  // Add date prop
            onCancel={() => onCancelClick(workOrder)}
            onEdit={() => onEdit(workOrder)}
            style={{
              minHeight: '200px',
              width: '100%',
              boxSizing: 'border-box'
            }}
          />
        ))}
      </div>


      {/* Cancel Popup */}
      <PopupGeneric
        isVisible={isCancelPopupVisible}
        onClose={onCancelPopupClose}
        title="Cancel Work Order"
        content={<p>Are you sure you want to cancel this work order?</p>}
        actions={
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              onClick={onConfirmCancel}
              style={{
                padding: '8px 16px',
                borderRadius: '5px',
                backgroundColor: '#1d5ca7',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Yes
            </button>
            <button
              onClick={onCancelPopupClose}
              style={{
                padding: '8px 16px',
                borderRadius: '5px',
                backgroundColor: '#8f0c03',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
              }}
            >
              No
            </button>
          </div>
        }
      />

      {/* Edit Modal */}
      <Modal open={isModalOpen} onClose={handleModalClose}>
        <Box className="task-modal-container">
          <WorkOrderForm formType="workorder" workOrder={selectedWorkOrder} onSubmit={handleFormSubmit} onClose={handleModalClose} />
        </Box>
      </Modal>
    </div>
  );
};

export default WorkOrdersPage;